/**
 * @file Firestore listener orchestration for active detail views.
 */

import {
    collection,
    doc,
    onSnapshot,
    orderBy,
    query,
    where,
} from "https://www.gstatic.com/firebasejs/12.19.0/firebase-firestore.js";
import { db } from "../firebase/firebase.js";
import {
    appDataDetails,
    detailViewListeners,
    state,
} from "../state/state.js";
import { renderDetailsView } from "../detail/detailController.js";
import {
    getCollectionName,
} from "../../shared/utils/itemLookup.js";

/**
 * Detaches all Firestore listeners for the active detail view.
 */
export function detachDetailViewListeners() {
    if (detailViewListeners.length > 0) {
        detailViewListeners.forEach((unsubscribe) => {
            try {
                unsubscribe();
            } catch (error) {
                console.warn("Error unsubscribing:", error);
            }
        });
        detailViewListeners.length = 0;
    }

    appDataDetails.tasks = [];
    appDataDetails.notes = [];
}

function detachNotesListener() {
    const noteListener = detailViewListeners.find((listener) =>
        listener.id === "notes");

    if (noteListener) {
        try {
            noteListener();
        } catch (error) {
            console.warn("Error unsubscribing notes:", error);
        }

        const index = detailViewListeners.indexOf(noteListener);
        if (index > -1) {
            detailViewListeners.splice(index, 1);
        }
    }
    appDataDetails.notes = [];
}

function _detachTasksListener() {
    const taskListener = detailViewListeners.find((listener) =>
        listener.id === "tasks");
    if (taskListener) {
        try {
            taskListener.unsubscribe();
        } catch (error) {
            console.warn("Error unsubscribing tasks:", error);
        }
        const index = detailViewListeners.indexOf(taskListener);
        if (index > -1) {detailViewListeners.splice(index, 1);}
    }
    appDataDetails.tasks = [];
}

function mapSnapshotDocs(snapshot) {
    return snapshot.docs.map((snapshotDoc) => ({
        id: snapshotDoc.id,
        ...snapshotDoc.data(),
    }));
}

function shouldRenderSelectedDetail(itemId) {
    return state.selectedItemId === itemId;
}

/**
 * Attaches listeners for a parent detail item and its child tasks/notes.
 * @param {string} parentId - Parent item document ID.
 * @param {string} parentType - Parent item type.
 */
export function attachParentDetailListeners(parentId, parentType) {
    detachDetailViewListeners();

    if (state.selectedItemId !== parentId) {
        return;
    }

    const collectionName = getCollectionName(parentType);
    const docRef = doc(db, collectionName, parentId);
    const itemListener = onSnapshot(docRef,
        (docSnap) => {
            if (!docSnap.exists()) {
                console.warn(`Parent item ${parentId} deleted or no longer exists.`);
                return;
            }
            if (
                shouldRenderSelectedDetail(parentId) &&
                (!document.activeElement ||
                    !document.activeElement.matches(
                        ".checklist-item-text-input, .checklist-ghost-input",
                    ))
            ) {
                renderDetailsView();
            }
        },
        (error) => {
            console.error(`Error listening to parent item ${parentId}:`, error);
        },
    );

    if (state.selectedItemId !== parentId) {
        itemListener();
        return;
    }

    itemListener.id = "parentItem";
    detailViewListeners.push(itemListener);

    const tasksQuery = query(
        collection(db, "tasks"),
        where("parentId", "==", parentId),
        where("parentType", "==", parentType),
        where("isSubtask", "==", true),
    );
    const tasksListener = onSnapshot(tasksQuery,
        (snapshot) => {
            appDataDetails.tasks = mapSnapshotDocs(snapshot);
            if (shouldRenderSelectedDetail(parentId)) {
                renderDetailsView();
            }
        },
        (error) => {
            console.error(`Error listening to tasks for parent ${parentId}:`, error);
            appDataDetails.tasks = [];
            if (shouldRenderSelectedDetail(parentId)) {
                renderDetailsView();
            }
        },
    );

    if (state.selectedItemId !== parentId) {
        tasksListener();
        itemListener();
        return;
    }

    tasksListener.id = "tasks";
    detailViewListeners.push(tasksListener);

    const notesQuery = query(
        collection(db, collectionName, parentId, "notes"),
        orderBy("createdAt", "desc"),
    );
    const notesListener = onSnapshot(notesQuery,
        (snapshot) => {
            appDataDetails.notes = mapSnapshotDocs(snapshot);
            if (shouldRenderSelectedDetail(parentId)) {
                renderDetailsView();
            }
        },
        (error) => {
            console.error(`Error listening to notes for item ${parentId}:`, error);
            appDataDetails.notes = [];
            if (shouldRenderSelectedDetail(parentId)) {
                renderDetailsView();
            }
        },
    );

    if (state.selectedItemId !== parentId) {
        notesListener();
        tasksListener();
        itemListener();
        return;
    }

    notesListener.id = "notes";
    detailViewListeners.push(notesListener);
}

/**
 * Attaches the task notes listener without replacing parent child-task data.
 * @param {string} taskId - Task document ID.
 */
export function attachTaskDetailListeners(taskId) {
    detachNotesListener();

    if (state.selectedItemId !== taskId) {
        return;
    }

    const notesQuery = query(
        collection(db, "tasks", taskId, "notes"),
        orderBy("createdAt", "desc"),
    );
    const notesListener = onSnapshot(notesQuery,
        (snapshot) => {
            appDataDetails.notes = mapSnapshotDocs(snapshot);
            if (shouldRenderSelectedDetail(taskId)) {
                renderDetailsView();
            }
        },
        (error) => {
            console.error(`Error listening to notes for task ${taskId}:`, error);
            appDataDetails.notes = [];
            if (shouldRenderSelectedDetail(taskId)) {
                renderDetailsView();
            }
        },
    );

    if (state.selectedItemId !== taskId) {
        notesListener();
        return;
    }

    notesListener.id = "notes";
    detailViewListeners.push(notesListener);
}
